import { HStack, Input, InputGroup, Text } from '@chakra-ui/react';
import { Field as ChakraField } from '@chakra-ui/react/field';
import { DMS } from '@components/ui/coordinatesInput/LatLongInput';
import { GPSCoordinatesListInputProps } from '@components/ui/coordinatesInput/list/GPSCoordinatesList.slots';
import { InputNumber } from '@components/ui/input/InputNumber';
import { isLatitude } from '@utils/common.utils';
import { toPrecision } from '@utils/math.utils';
import { dmsToString } from '@utils/validation/coordinates.utils';
import DmsCoordinates, { parseDms } from 'dms-conversion';
import { Field, FieldInputProps, FormikProps, getIn, useFormikContext } from 'formik';
import { useEffect, useState } from 'react';

import { CoordinatesUnitEnum } from '@/types/commons/commons.types';

export const LatLongInputList = ({ type, name, readOnly, unit, onPolygonChange }: GPSCoordinatesListInputProps) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { values, setFieldValue } = useFormikContext<any>();

  const value = getIn(values, name);
  const isLat = isLatitude(type);

  const numberValue = value ? Number(value) : null;
  const [dms, setDms] = useState<DMS>({
    degrees: null,
    minutes: null,
    seconds: null,
  });

  useEffect(() => {
    if (numberValue != null && !Number.isNaN(numberValue)) {
      try {
        const dmsCoords = isLat ? new DmsCoordinates(numberValue, 0) : new DmsCoordinates(0, numberValue);
        const dmsArray = isLat ? dmsCoords.dmsArrays.latitude : dmsCoords.dmsArrays.longitude;
        const [degrees, minutes, seconds] = dmsArray;

        setDms({ degrees, minutes, seconds: toPrecision(Number(seconds), 3) });
      } catch (err) {
        console.warn('Invalid coordinate:', err);
      }
    }
  }, [isLat, numberValue]);

  const handleChange = (field: 'degrees' | 'minutes' | 'seconds', newValue: string | number | null) => {
    if (newValue === null || newValue === '') {
      newValue = null;
    }

    const updated: DMS = { ...dms, [field]: newValue };

    setDms(updated);
    void setFieldValue(name, parseDms(dmsToString(updated)).toString());
    onPolygonChange(name, parseDms(dmsToString(updated)).toString());
  };

  if (unit === CoordinatesUnitEnum.DECIMAL_DEGREES) {
    return (
      <Field name={name}>
        {({ field, form }: { field: FieldInputProps<string>; form: FormikProps<unknown> }) => {
          const error = getIn(form.errors, field.name);
          const touched = getIn(form.touched, field.name);

          return (
            <ChakraField.Root invalid={!!touched && !!error}>
              <InputGroup
                className="group"
                gap={1}
                startElementProps={{ width: 'auto', justifyContent: 'flex-start', minWidth: '34px', paddingX: 2 }}
                endElement={
                  <Text color="neutral.300" textTransform="capitalize">
                    {isLat ? 'N' : 'E'}
                  </Text>
                }
                endElementProps={{ paddingInline: 2 }}
              >
                <InputNumber
                  name={name}
                  id={type}
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                  value={toPrecision(Number.parseFloat(field.value), 5) ?? ''}
                  readOnly={readOnly}
                  style={{
                    paddingInlineStart: '48px',
                    paddingInlineEnd: '30px',
                  }}
                  onChange={(e) => {
                    onPolygonChange(field.name, String(e ?? 0));
                  }}
                  onBlur={() => form.setFieldTouched(field.name, true)}
                />
              </InputGroup>
            </ChakraField.Root>
          );
        }}
      </Field>
    );
  }

  return (
    <Field name={name}>
      {({ field, form }: { field: FieldInputProps<string>; form: FormikProps<unknown> }) => {
        const error = getIn(form.errors, field.name);
        const touched = getIn(form.touched, field.name);
        const invalid = !!touched && !!error;

        return (
          <ChakraField.Root invalid={invalid}>
            <HStack
              className="group"
              backgroundColor={invalid ? 'red.800' : 'neutral.800'}
              border="2px solid"
              borderColor={invalid ? 'red' : 'neutral.black'}
              paddingX={2}
              paddingY={0.25}
              gap={0.5}
              width="100%"
              height="48px"
              _focus={{ borderColor: 'skyBlue' }}
              _hover={{ borderColor: 'skyBlue' }}
            >
              <Input
                type="number"
                variant="unstyled"
                width="auto"
                minWidth="20px"
                maxWidth="30px"
                justifyItems="center"
                padding={0}
                min={isLat ? -90 : -180}
                max={isLat ? 90 : 180}
                value={dms.degrees ?? ''}
                readOnly={readOnly}
                onChange={(e) => handleChange('degrees', e.target.value)}
                onBlur={() => form.setFieldTouched(field.name, true)}
              />
              <Text color="neutral.300">°</Text>
              <Input
                type="number"
                variant="unstyled"
                width="auto"
                minWidth="20px"
                maxWidth="30px"
                justifyItems="center"
                padding={0}
                min={0}
                max={59}
                readOnly={readOnly}
                value={dms.minutes ?? ''}
                onChange={(e) => handleChange('minutes', e.target.value)}
                onBlur={() => form.setFieldTouched(field.name, true)}
              />
              <Text color="neutral.300">&#39;</Text>
              <Input
                type="number"
                variant="unstyled"
                width="auto"
                minWidth="20px"
                maxWidth="60px"
                justifyItems="center"
                padding={0}
                min={0}
                max={60}
                step="0.00001"
                value={dms.seconds ?? ''}
                readOnly={readOnly}
                onChange={(e) => {
                  const value = e.target.value;
                  const isValidDecimal = /^(\d+(\.\d*)?)?$/.test(value);

                  if (isValidDecimal) {
                    setDms((prev) => ({ ...prev, seconds: value }));
                  }
                }}
                onBlur={() => form.setFieldTouched(field.name, true)}
              />
              <Text color="neutral.300">&#34;</Text>
              <Text color="neutral.300" textTransform="capitalize">
                {isLat ? 'N' : 'E'}
              </Text>
            </HStack>
          </ChakraField.Root>
        );
      }}
    </Field>
  );
};
